import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { APIStatus } from "../utils/types";
import { FileApi, TimePeriodApi, TimePeriodApiApiTimePeriodCreatePostRequest, TimePeriodApiApiTimePeriodGetGetRequest, TimePeriodServiceGetUploadFilesResponse, UploadApi, UploadApiApiUploadApplyChangesPostRequest, UploadApiApiUploadGetChangesToValidateGetRequest, UploadServiceCreateUploadFileResponse } from "../services/api";
import { useApiConfiguration } from "../services/api-configuration";
import { store } from ".";

export type FileUploadAndStatus = File & {
  // status: APIStatus | null;
  // uploadType?: 'bafo' | 'group_data';
};

export type State = {
  lastUpdatedAt: Date | null;
  apiState: APIStatus | null;
  uploadHistory: TimePeriodServiceGetUploadFilesResponse[];
  uploadState: APIStatus | null;
  showFileUploadPreview: boolean;
  queuedUploads: {
    period: Date;
    files: {
      file: Blob[];
      uploadId: string;
      fileId: string;
    }
  } | null;
  uploadedFiles: {
    period: string;
    files: File[];
  } | null;
  currentPeriod: string | null;
  hasPendingUploads: boolean;
}

export type Actions = { 
}

const initialState: State = {
  lastUpdatedAt: null,
  apiState: null,
  uploadHistory: [],
  queuedUploads: null,
  currentPeriod: null,
  hasPendingUploads: false,
  uploadedFiles: null,
  uploadState: null,
  showFileUploadPreview: false,
}

export const fetchUploadHistory = createAsyncThunk(
  'upload/fetchAll',
  async (timePeriodDate: Date) => {
    const api = new TimePeriodApi(useApiConfiguration());
    return await api.apiTimePeriodGetUploadFilesGet({
      timePeriodDate,
    });
  }
)

export async function doFileUpload(req: UploadApiApiUploadApplyChangesPostRequest) {
  const api = new UploadApi(useApiConfiguration());
  return await api.apiUploadApplyChangesPost(req);
}

export async function getFileChangesPreview(req: UploadApiApiUploadGetChangesToValidateGetRequest) {
  const api = new UploadApi(useApiConfiguration());
  return await api.apiUploadGetChangesToValidateGet(req);
}

export async function declineUploadFile(uploadId: string) {
  const api = new UploadApi(useApiConfiguration());
  return await api.apiUploadDeclineUploadFilePost({
    uploadId
  });
}

export const getLastUpdatedTimestamp = createAsyncThunk(
  'upload/fetchLastUpdated',
  async () => {
    const { ui: { currentPeriod} } = store.getState();
    const api = new TimePeriodApi(useApiConfiguration());
    return await api.apiTimePeriodGetUploadFilesGet({
      timePeriodDate: currentPeriod
    });
  }
)

export const addFileToTimePeriod = createAsyncThunk(
  'upload/addFileToTimePeriod',
  async (req: { files: Blob[], timePeriodDate: Date }) => {
    const api = new UploadApi(useApiConfiguration());
    const requests: Promise<UploadServiceCreateUploadFileResponse>[] = [];

    for (const i in req.files) {
      requests.push(api.apiUploadCreateUploadFilePost({
        file: req.files[i],
        timePeriodDate: req.timePeriodDate,
      }))
    }

    console.log(req.files);

    const result = await Promise.all(requests);

    return {
      timePeriodDate: req.timePeriodDate,
      files: result.map((res: UploadServiceCreateUploadFileResponse, index: number) => {
        return {
          file: req.files[index],
          ...res,
        }
      })
    };
  }
)

export async function fetchTimePeriod(req: TimePeriodApiApiTimePeriodGetGetRequest) {
  const api = new TimePeriodApi(useApiConfiguration());
  return await api.apiTimePeriodGetGet(req)
}

export async function createTimePeriod(req: TimePeriodApiApiTimePeriodCreatePostRequest) {
  const api = new TimePeriodApi(useApiConfiguration());
  return await api.apiTimePeriodCreatePost(req)
}

export async function doFileDownload({
  fileName,
  fileId
}: {
  fileName: string,
  fileId: string
}) {
  const api = new FileApi(useApiConfiguration());
  return await api.apiFileGetFileLinkGet({
    fileId,
    fileNameForLink: fileName,
    timeToLiveForLink: 15,
  });
}

export const uploadsStore = createSlice({
    name: 'uploads',
    initialState,
    reducers: {
      queueFileUpload: (state: State, action: { payload: { files: File[], date: string } }) => {
        state.queuedUploads = {
          period: action.payload.date,
          files: action.payload.files,
        };
      },
      processUploadQueue: (state: State) => {
        // state.uploadedFiles = {
        //   ...state.queuedUploads,
        //   // files: state.queuedUploads?.files,
        // };
        state.showFileUploadPreview = false;
        state.queuedUploads = null;
      },
      clearUploadQueue: (state: State) => {
        state.queuedUploads = null;
      }
    },
    extraReducers: (builder) => {
      builder
        .addCase(fetchUploadHistory.fulfilled, (state, action) => {
          state.apiState = APIStatus.COMPLETE;
          state.uploadHistory = action.payload.filter(file => file.status !== 'Declined').sort((a, b) => {
            return a.createdDate?.getTime() < b.createdDate?.getTime() ? 1 : -1;
          });
        })
        .addCase(fetchUploadHistory.pending, (state) => {
          state.apiState = APIStatus.LOADING;
        })
        .addCase(fetchUploadHistory.rejected, (state) => {
          state.apiState = APIStatus.ERROR;
        });

      builder.addCase(getLastUpdatedTimestamp.fulfilled, (state, action) => {
        const [lastUpdated, ..._rest] = action.payload.sort((a, b) => {
          return new Date(b.createdDate!).getTime() - new Date(a.createdDate!).getTime();
        });
        state.lastUpdatedAt = lastUpdated ? new Date(lastUpdated.createdDate!) : null;
      });

      builder.addCase(
          addFileToTimePeriod.fulfilled, (state, action) => {
          state.uploadState = APIStatus.COMPLETE;
          state.showFileUploadPreview = true;

          console.log({ action });

          state.queuedUploads = {
            period: action.payload.timePeriodDate,
            files: action.payload.files
          }
      }).addCase(
        addFileToTimePeriod.pending, (state) => {
          state.uploadState = APIStatus.LOADING;
        }
      ).addCase(addFileToTimePeriod.rejected, (state) => {
        state.uploadState = APIStatus.COMPLETE;
      });
    }
});

export const {
  clearUploadQueue,
  processUploadQueue,
  queueFileUpload,
} = uploadsStore.actions;

export default uploadsStore.reducer;